import React, { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { makeStyles } from 'tss-react/mui';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import { useFetchOnce } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import AddBusinessDrawer from 'components/Business/AddBusinessDrawer';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';
import BusinessCard from 'containers/BusinessCard';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles()(({ spacing }) => ({
  iconSpacing: {
    marginRight: spacing(1),
  },
}));

export default function Businesses() {
  const { classes } = useStyles();
  const [addBusiness, setAddBusiness] = useState(false);
  const [currentBusinessId, setCurrentBusinessId] = useState<string | null>(null);

  useFetchOnce(businessActions.getBusiness, businessSelectors);
  const businesses = useAppSelector(businessSelectors.getBusinessList);

  const openAddBusiness = useCallback(() => {
    tracker.track(tracker.events.addBusinessStart);
    setAddBusiness(true);
  }, []);

  const handleClose = useCallback(() => {
    if (currentBusinessId) {
      tracker.track(tracker.events.addBusinessComplete);
    }
    setCurrentBusinessId(null);
    setAddBusiness(false);
  }, [currentBusinessId]);

  const handleBusinessCreated = useCallback(
    (id: string) => {
      setCurrentBusinessId(id);
      const created = businesses.find((b) => b.id === id);
      if (created) {
        tracker.track(tracker.events.addBusinessSaved, {
          biz_name: created.name,
          industry: created.industry,
          business_structure: created.structure,
          inferred_tax_group: created.taxGroup,
        });
      }
    },
    [businesses],
  );

  return (
    <Box>
      {addBusiness && (
        <AddBusinessDrawer
          open
          onClose={handleClose}
          onBusinessCreated={handleBusinessCreated}
          trackSetupClicks={false}
          introText={null}
        />
      )}
      <SettingsHeader
        title={businesses.size > 1 ? `Businesses (${businesses.size})` : 'Businesses'}
        primaryItem={
          <div>
            {businesses?.size > 0 ? (
              <QButton id='accounts-add-new-button' variant='contained' onClick={openAddBusiness}>
                <AddIcon className={classes.iconSpacing} /> Business
              </QButton>
            ) : null}
          </div>
        }
      />

      {businesses.map((business) => {
        if (business.id === currentBusinessId) {
          return null;
        }
        return <BusinessCard key={`${business.name}-card`} business={business} />;
      })}

      {businesses?.size === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '80vh' }}>
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <ZeroStateView
              size='large'
              animate={true}
              qIllustration='businessBriefcase'
              title='Add your business'
              description='Enter your business information to easily track your transactions and stay organized for tax season.'
              button={
                <Button variant='contained' startIcon={<AddCircleRoundedIcon />} onClick={openAddBusiness}>
                  Business
                </Button>
              }
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
}
